import type {
  ModerationHistoryCursor,
  ModerationHistoryEntry,
  ModerationHistoryPage,
} from "@little-finds/core";

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const id = (value: unknown): value is string =>
  typeof value === "string" &&
  /^[1-9][0-9]{0,18}$/.test(value) &&
  BigInt(value) <= 9223372036854775807n;
const date = (value: unknown): value is string =>
  typeof value === "string" && Number.isFinite(Date.parse(value));
const optionalUUID = (value: unknown): value is string | null =>
  value === null || (typeof value === "string" && uuid.test(value));

/** Copy an allowlist only; private notes must never enter this view's state. */
export function readHistoryPage(value: unknown): ModerationHistoryPage {
  const invalid = () => new Error("The history response could not be read.");
  if (
    !record(value) ||
    !Array.isArray(value.entries) ||
    value.entries.length > 100 ||
    typeof value.has_more !== "boolean"
  )
    throw invalid();
  const entries: ModerationHistoryEntry[] = value.entries.map((entry) => {
    if (
      !record(entry) ||
      !id(entry.id) ||
      !optionalUUID(entry.actor_id) ||
      !optionalUUID(entry.target_id) ||
      !date(entry.created_at) ||
      typeof entry.kind !== "string" ||
      !entry.kind ||
      entry.kind.length > 80 ||
      typeof entry.action !== "string" ||
      !entry.action ||
      entry.action.length > 80
    )
      throw invalid();
    return {
      id: entry.id,
      actor_id: entry.actor_id,
      target_id: entry.target_id,
      kind: entry.kind,
      action: entry.action,
      created_at: entry.created_at,
    };
  });
  if (new Set(entries.map((entry) => entry.id)).size !== entries.length)
    throw invalid();
  let next_cursor: ModerationHistoryCursor | null = null;
  if (value.has_more) {
    const last = entries.at(-1),
      cursor = value.next_cursor;
    if (
      !last ||
      !record(cursor) ||
      cursor.id !== last.id ||
      cursor.created_at !== last.created_at
    )
      throw invalid();
    next_cursor = { id: last.id, created_at: last.created_at };
  } else if (value.next_cursor !== null) throw invalid();
  return { entries, has_more: value.has_more, next_cursor };
}

export type HistoryState = {
  entries: ModerationHistoryEntry[];
  cursor: ModerationHistoryCursor | null;
  hasMore: boolean;
  loading: boolean;
  error: string;
};
export const emptyHistory = (): HistoryState => ({
  entries: [],
  cursor: null,
  hasMore: false,
  loading: false,
  error: "",
});

/** Each mounted viewer owns a pager; late replies cannot restore another viewer's history. */
export function createHistoryPager(
  request: (cursor: ModerationHistoryCursor | null) => Promise<unknown>,
  publish: (state: HistoryState) => void,
) {
  let state = emptyHistory(),
    generation = 0,
    disposed = false;
  const update = (next: HistoryState) => {
    state = next;
    publish(next);
  };
  async function load(reset: boolean) {
    if (disposed || (!reset && (state.loading || !state.hasMore))) return;
    const current = ++generation,
      previous = reset ? emptyHistory() : state;
    update({ ...previous, loading: true, error: "" });
    try {
      const page = readHistoryPage(await request(previous.cursor));
      if (disposed || generation !== current) return;
      const priorIDs = new Set(previous.entries.map((entry) => entry.id));
      update({
        entries: [
          ...previous.entries,
          ...page.entries.filter((entry) => !priorIDs.has(entry.id)),
        ],
        cursor: page.next_cursor,
        hasMore: page.has_more,
        loading: false,
        error: "",
      });
    } catch {
      if (disposed || generation !== current) return;
      // Fail closed, including when an administrator's access was revoked.
      update({
        ...emptyHistory(),
        error:
          "History could not be loaded. Refresh to try again, or check your administrator access.",
      });
    }
  }
  return {
    refresh: () => load(true),
    more: () => load(false),
    dispose: () => {
      disposed = true;
      generation++;
      state = emptyHistory();
    },
  };
}
